import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Fragment } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { Divider } from "@/components/Divider/Divider";
import { ReservationDocumentList } from "@/features/bookings/components/ReservationDocumentList";
import { ReservationNextSteps } from "@/features/bookings/components/ReservationNextSteps";
import { reservationQueryOptions } from "@/features/bookings/hooks/useReservations";
import { HotelRoom } from "@/features/hotels/components/hotel/HotelRoom";
import { BookedHotelCard } from "@/features/hotels/components/hotels-list/BookedHotelCard";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import type { HotelBookingRoomOption } from "@/features/hotels/types/types";
import { useOrder } from "@/hooks/useOrder";

export const HotelBookingSuccess = ({
  orderId,
  rooms,
}: {
  orderId: string;
  rooms: HotelBookingRoomOption[];
}) => {
  const navigate = useNavigate();

  const intl = useIntl();
  const { searchData } = useHotelSearchDataContext();
  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);
  const reservationId = orderData?.reservations?.find(
    (reservation) => reservation.service === "hotel",
  )?.id;
  const { data: reservationData, isLoading: isReservationLoading } = useQuery(
    reservationQueryOptions(reservationId),
  );
  const reservation = reservationData?.hotelReservation;

  const isLoading =
    isOrderLoading ||
    !orderData?.reservations.length ||
    isReservationLoading ||
    reservationData?.hotelReservation.status === "IN_PROGRESS";

  return (
    <div className="px-4 pb-4">
      <div className="mb-2 p-2 text-center text-xl font-semibold text-text-primary">
        {intl.formatMessage({
          defaultMessage: "Booked",
          description:
            "Success message shown after a hotel room is successfully booked",
        })}
      </div>

      <div className="section-title">
        {intl.formatMessage({
          defaultMessage: "Hotel details",
          description: "Section or page heading for hotel details",
        })}
      </div>

      {isLoading ? (
        <section className="section-card text-center">
          <CircularProgress size={20} />
        </section>
      ) : (
        <BookedHotelCard
          reservation={reservation!.externalReservation}
          footer={
            <div className="flex flex-col gap-4 text-text-primary">
              {rooms.map((room, i) => (
                <Fragment key={room.roomToken}>
                  <Divider color="neutral-softer" />

                  <HotelRoom
                    room={room}
                    adultCount={searchData!.rooms[i].adults}
                    childrenCount={searchData!.rooms[i].children}
                  />
                </Fragment>
              ))}
            </div>
          }
        />
      )}

      <section className="section-card mt-2 mb-4 flex w-full items-center justify-between gap-4">
        <span className="section-title mb-0">
          {intl.formatMessage({
            defaultMessage: "Booking number",
            description:
              "Label for the airline or hotel booking reference number",
          })}
        </span>
        <span className="text-base font-semibold text-text-title">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            reservation?.externalReservation.reference.external
          )}
        </span>
      </section>

      {orderData?.reservations.map((reservation) => (
        <ReservationDocumentList
          key={reservation.id}
          reservationId={reservation.reservationId}
        />
      ))}

      <ReservationNextSteps type="hotel" />

      <div className="mt-4 flex flex-col items-center justify-center gap-2">
        <div className="w-60">
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => {
              navigate({ to: "/" });
            }}
          >
            <span>
              {intl.formatMessage({
                defaultMessage: "Back to home",
                description: "Button to return to the home / search screen",
              })}
            </span>
          </Button>
        </div>
        <div className="w-60">
          <Button
            variant="text"
            size="small"
            fullWidth
            onClick={() => {
              navigate({ to: "/bookings", search: { category: "hotels" } });
            }}
          >
            <span>
              {intl.formatMessage({
                defaultMessage: "Open My Bookings",
                description:
                  "Button to navigate to the My Bookings screen after booking completion",
              })}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
